import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, Popover, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, useLightStyles } from "../lightTheme";
import { Context } from "states/Store";
import { getDoctypeTranslatedName } from "services/common/DocumentUtils";
import { getAccountAttributesByBondType } from "services/edge/AttributesService";
import { getAccountRequirements } from "services/edge/AccountService";
import { isSealedDocument } from "services/common/Constants";
import { getDefaultValuesIfExist, getDefaultShownValuesIfExist } from "components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils";
import LightAttributesForm from "./LightAttributesForm";

const useStyles = makeStyles()(() => ({
  dossierList: {
    display: "flex", flexDirection: "column", gap: "0.5rem",
  },
  dossierCard: {
    display: "flex", alignItems: "center", gap: "0.85rem",
    padding: "0.85rem 1.15rem", borderRadius: "12px",
    backgroundColor: BRAND.white, border: `1.5px solid ${BRAND.border}`,
    cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
    "&:hover": { borderColor: BRAND.primary, boxShadow: "0 4px 16px rgba(0,58,34,0.1)", transform: "translateY(-1px)" },
  },
  dossierIcon: {
    width: "36px", height: "36px", borderRadius: "10px",
    backgroundColor: `${BRAND.primary}10`,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  dossierInfo: {
    flex: 1, minWidth: 0,
  },
  dossierName: {
    fontSize: "0.88rem", fontWeight: 700, color: BRAND.textMain,
    fontFamily: "Inter, sans-serif", lineHeight: 1.3,
  },
  dossierReqs: {
    fontSize: "0.7rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif", marginTop: "0.1rem",
  },
  dossierArrow: {
    fontSize: "0.75rem", fontWeight: 600, color: BRAND.primary,
    fontFamily: "Inter, sans-serif", flexShrink: 0, opacity: 0.5,
    display: "flex", alignItems: "center", gap: "0.2rem",
    transition: "opacity 0.15s",
  },
  infoBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "24px", height: "24px", borderRadius: "50%",
    border: "none", background: "transparent",
    cursor: "pointer", color: BRAND.textMuted, flexShrink: 0,
    padding: 0,
    transition: "background 0.15s, color 0.15s",
    "&:hover": {
      background: BRAND.background,
      color: BRAND.primary,
    },
  },
  popover: {
    pointerEvents: "none",
  },
  popoverPaper: {
    padding: "1rem 1.25rem",
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    maxWidth: "260px",
    fontFamily: "Inter, sans-serif",
  },
  popoverTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    marginBottom: "0.6rem",
    fontFamily: "Inter, sans-serif",
  },
  popoverItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    fontSize: "0.8rem",
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    padding: "0.25rem 0",
    borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    fontSize: "0.8rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    marginBottom: "1.75rem",
    width: "fit-content",
    "&:hover": { color: BRAND.primary },
    transition: "color 0.15s",
  },
  // ── TP summary card ──
  tpSummary: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "0.75rem 0",
    marginBottom: "1rem",
    borderBottom: `1px solid ${BRAND.border}`,
  },
  tpInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1,
    minWidth: 0,
  },
  tpLabel: {
    fontSize: "0.65rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  tpName: {
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tpMeta: {
    fontSize: "0.72rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  tpChangeCta: {
    fontSize: "0.75rem",
    color: BRAND.primary,
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": { textDecoration: "underline" },
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.9rem",
  },
  attrHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1.25rem",
  },
  attrTitle: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },
  attrSubtitle: {
    fontSize: "0.8rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    marginTop: "0.15rem",
  },
  attrActions: {
    display: "flex",
    gap: "0.75rem",
    width: "100%",
    marginTop: "1.5rem",
    justifyContent: "space-between",
  },
  attrBackBtn: {
    height: "40px",
    padding: "0 1.25rem",
    borderRadius: "10px",
    border: `1.5px solid ${BRAND.border}`,
    backgroundColor: BRAND.white,
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    transition: "border-color 0.15s, color 0.15s",
    "&:hover": { borderColor: BRAND.primary, color: BRAND.primary },
  },
  attrContinueBtn: {
    height: "40px",
    padding: "0 1.5rem",
    borderRadius: "10px",
    border: "none",
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
  },
  extraDocsBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "28px", height: "28px", borderRadius: "50%",
    border: "none", background: "transparent",
    cursor: "pointer", color: BRAND.primary, flexShrink: 0, padding: 0,
    transition: "background 0.15s",
    "&:hover": { background: `${BRAND.primary}12` },
  },
  extraDocsBadge: {
    position: "absolute", top: "-4px", right: "-4px",
    width: "16px", height: "16px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    fontSize: "0.6rem", fontWeight: 700, fontFamily: "Inter, sans-serif",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  extraDocsPopover: {
    padding: "1rem 0",
    borderRadius: RADIUS.lg,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    width: "340px",
    fontFamily: "Inter, sans-serif",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column",
  },
  extraDocsHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 1rem 0.75rem",
    borderBottom: `1px solid ${BRAND.border}`,
  },
  extraDocsTitle: {
    fontSize: "0.8rem", fontWeight: 700, color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },
  extraDocsList: {
    overflowY: "auto", flex: 1,
  },
  extraDocsItem: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.35rem 1rem",
    fontSize: "0.8rem", color: BRAND.textMain, fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    "&:hover": { backgroundColor: BRAND.background },
  },
  extraDocsItemIncluded: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.35rem 1rem",
    fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
    fontStyle: "italic",
  },
}));

function DocsPopover({ requirements }) {
  const { classes: s } = useStyles();
  const [state] = useContext(Context);
  const [anchor, setAnchor] = useState(null);

  if (!requirements?.length) return null;

  return (
    <>
      <button
        className={s.infoBtn}
        onMouseEnter={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        onMouseLeave={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        aria-label={i18next.t("light.launch.viewDocs")}
      >
        <InfoOutlinedIcon style={{ fontSize: "16px" }} />
      </button>

      <Popover
        className={s.popover}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableRestoreFocus
        PaperProps={{ className: s.popoverPaper, elevation: 0 }}
        sx={{ pointerEvents: "none" }}
      >
        <Typography className={s.popoverTitle}>
          {i18next.t("light.launch.docsRequired", { count: requirements.length })}
        </Typography>
        {requirements.map((req, i) => (
          <Box key={req.code ?? i} className={s.popoverItem}>
            <ArticleOutlinedIcon style={{ fontSize: "14px", color: BRAND.primary, flexShrink: 0, marginTop: "1px" }} />
            {getDoctypeTranslatedName(state.doctypeTraductions, req.code, state.lang) || req.descriptionFr || req.name || req.code}
          </Box>
        ))}
      </Popover>
    </>
  );
}

function ExtraDocsPopover({ typeCode, requirements, extraDocs, onExtraDocsChange }) {
  const { classes: s } = useStyles();
  const [state] = useContext(Context);
  const [anchor, setAnchor] = useState(null);
  const [accountReqs, setAccountReqs] = useState(null);
  const [loading, setLoading] = useState(false);

  const includedCodes = new Set((requirements ?? []).map((r) => r.code));
  const extras = extraDocs[typeCode] ?? [];

  const handleOpen = async (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    if (accountReqs === null) {
      setLoading(true);
      try {
        const res = await getAccountRequirements(state.account.id, null);
        setAccountReqs((res?.content ?? []).filter((r) => !isSealedDocument(r.code)));
      } catch (_) { setAccountReqs([]); }
      finally { setLoading(false); }
    }
  };

  const handleToggle = (code) => {
    const cur = extraDocs[typeCode] ?? [];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    onExtraDocsChange({ ...extraDocs, [typeCode]: next });
  };

  const availableDocs = (accountReqs ?? []).filter((r) => !includedCodes.has(r.code));

  return (
    <>
      <Box style={{ position: "relative" }}>
        <button className={s.extraDocsBtn} onClick={handleOpen} aria-label={i18next.t("light.launch.addExtraDocs")}>
          <PostAddIcon style={{ fontSize: "18px" }} />
        </button>
        {extras.length > 0 && <Box className={s.extraDocsBadge}>{extras.length}</Box>}
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: s.extraDocsPopover, elevation: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className={s.extraDocsHeader}>
          <Typography className={s.extraDocsTitle}>{i18next.t("light.launch.extraDocs")}</Typography>
          <button className={s.infoBtn} onClick={() => setAnchor(null)} style={{ width: 20, height: 20 }}>
            <CloseIcon style={{ fontSize: "14px" }} />
          </button>
        </Box>
        <Box className={s.extraDocsList}>
          {loading ? (
            <Box style={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
              <CircularProgress size={18} style={{ color: BRAND.primary }} />
            </Box>
          ) : availableDocs.length === 0 ? (
            <Box style={{ padding: "1rem", textAlign: "center", fontSize: "0.8rem", color: BRAND.textMuted }}>
              {i18next.t("light.launch.noExtraDocs")}
            </Box>
          ) : (
            availableDocs.map((doc) => {
              const name = getDoctypeTranslatedName(state.doctypeTraductions, doc.code, state.lang) || doc.descriptionFr || doc.name || doc.code;
              const checked = extras.includes(doc.code);
              return (
                <Box key={doc.code} className={s.extraDocsItem} onClick={() => handleToggle(doc.code)}>
                  <Checkbox size="small" checked={checked} sx={{ padding: 0, color: BRAND.border, "&.Mui-checked": { color: BRAND.primary } }} />
                  <ArticleOutlinedIcon style={{ fontSize: "14px", color: checked ? BRAND.primary : BRAND.textMuted, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{name}</span>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
}

export default function StepChooseDossier({ thirdparty, onBack, onSelect, initialType, backLabel, stepper }) {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state] = useContext(Context);
  // Multi-select: selectedTypes is an array of type objects
  const [selectedTypes, setSelectedTypes] = useState(initialType ? (Array.isArray(initialType) ? initialType : [initialType]) : []);
  const [extraDocs, setExtraDocs] = useState({});
  const [showAttrForm, setShowAttrForm] = useState(false);
  const [dossierAttrDefs, setDossierAttrDefs] = useState([]);
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const attrFormRef = useRef(null);
  const savedAttrValues = useRef(
    (Array.isArray(initialType) ? initialType[0] : initialType)?.pendingDossierAttributes?.length > 0
      ? (Array.isArray(initialType) ? initialType[0] : initialType).pendingDossierAttributes
      : null
  );

  useEffect(() => {
    if (!state.account?.id) return;
    setLoadingAttrs(true);
    getAccountAttributesByBondType(state.account.id, "dossier", null)
      .then((res) => {
        const defs = (res?.content ?? []).map((def) => ({
          ...def,
          attributeId: def.id,
          values: getDefaultValuesIfExist(def),
          shownValues: getDefaultShownValuesIfExist(def),
        }));
        setDossierAttrDefs(defs);
      })
      .catch(() => setDossierAttrDefs([]))
      .finally(() => setLoadingAttrs(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  const dossierTypes = state.dossierTypes ?? [];

  const handleToggleType = (type) => {
    setSelectedTypes((prev) => {
      const exists = prev.some((t) => t.code === type.code);
      return exists ? prev.filter((t) => t.code !== type.code) : [...prev, type];
    });
  };

  const handleNext = () => {
    if (selectedTypes.length === 0) return;
    if (!loadingAttrs && dossierAttrDefs.length === 0) {
      onSelect?.(selectedTypes.map((t) => ({ ...t, pendingDossierAttributes: [], extraDocs: extraDocs[t.code] ?? [] })));
      return;
    }
    setShowAttrForm(true);
  };

  const handleContinue = () => {
    if (!attrFormRef.current) {
      onSelect?.(selectedTypes.map((t) => ({ ...t, pendingDossierAttributes: [], extraDocs: extraDocs[t.code] ?? [] })));
      return;
    }
    const valid = attrFormRef.current.validate();
    if (!valid) return;
    const attrs = attrFormRef.current.getValues();
    onSelect?.(selectedTypes.map((t) => ({ ...t, pendingDossierAttributes: attrs, extraDocs: extraDocs[t.code] ?? [] })));
  };

  const handleBackToTypes = () => {
    if (attrFormRef.current) {
      savedAttrValues.current = attrFormRef.current.getValues();
    }
    setShowAttrForm(false);
  };

  const tpSummary = thirdparty && (
    <Box className={s.tpSummary}>
      <Box className={light.iconWrap} style={{ backgroundColor: `${BRAND.primary}10` }}>
        <BusinessOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
      </Box>
      <Box className={s.tpInfo}>
        <Typography className={s.tpLabel}>{i18next.t("light.launch.selectedTp")}</Typography>
        <Typography className={s.tpName}>{thirdparty.name}</Typography>
        {(thirdparty.identifier || thirdparty.city) && (
          <Typography className={s.tpMeta}>
            {[thirdparty.identifier, thirdparty.postCode, thirdparty.city].filter(Boolean).join(" · ")}
          </Typography>
        )}
      </Box>
      {!backLabel && (
        <Typography className={s.tpChangeCta} onClick={onBack}>
          {i18next.t("light.launch.modifyTp")}
        </Typography>
      )}
    </Box>
  );

  // ── Attributes form (after types are selected) ──
  if (showAttrForm) {
    const typeNames = selectedTypes.map((t) => t.libelle).join(", ");
    return (
      <Box className={cx(light.pageRoot)}>
        <Box className={light.pageHeader}>
          <Box className={light.pageHeaderInner} style={{ maxWidth: "1100px" }}>
            <Box className={s.backBtn} onClick={handleBackToTypes} style={{ color: "rgba(255,255,255,0.7)", marginBottom: "0.75rem" }}>
              <ArrowBackIcon style={{ fontSize: "16px" }} />
              {i18next.t("light.launch.backToEvalChoice")}
            </Box>
            <Typography className={light.pageTitleWhite}>{typeNames}</Typography>
            <Typography className={light.pageSubtitleWhite}>
              {i18next.t("light.launch.completeAttrs")}
            </Typography>
          </Box>
        </Box>
        <Box className={light.pageInner} style={{ maxWidth: "1100px", marginTop: "-0.75rem" }}>
          <Box className={light.card} style={{ overflow: "hidden", padding: 0 }}>
            {stepper}
            <Box style={{ padding: "1.25rem" }}>

          {tpSummary}

          <Box>
            <Box className={s.attrHeader}>
              <Box className={cx(light.iconWrap, light.iconWrapSecondary)}>
                <FolderOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
              </Box>
              <Box>
                <Typography className={s.attrTitle}>
                  {selectedTypes.length > 1
                    ? `${selectedTypes.length} ${i18next.t("light.launch.evaluationsSelected")}`
                    : selectedTypes[0]?.libelle
                  }
                </Typography>
              </Box>
            </Box>

            {loadingAttrs ? (
              <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0" }}>
                <CircularProgress size={16} style={{ color: BRAND.primary }} />
                <Typography style={{ fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: "Inter" }}>{i18next.t("light.common.loading")}</Typography>
              </Box>
            ) : (
              <LightAttributesForm ref={attrFormRef} attributes={savedAttrValues.current ?? dossierAttrDefs} />
            )}

            <Box className={s.attrActions}>
              <button className={s.attrBackBtn} onClick={handleBackToTypes}>
                <ArrowBackIcon style={{ fontSize: "16px" }} />
                {i18next.t("back")}
              </button>
              <button className={s.attrContinueBtn} onClick={handleContinue}>
                {i18next.t("continue")} →
              </button>
            </Box>
          </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Type selection ──
  return (
    <Box className={cx(light.pageRoot)}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner} style={{ maxWidth: "1100px" }}>
          <Box className={s.backBtn} onClick={onBack} style={{ color: "rgba(255,255,255,0.7)", marginBottom: "0.75rem" }}>
            <ArrowBackIcon style={{ fontSize: "16px" }} />
            {backLabel || i18next.t("light.launch.backToTpChoice")}
          </Box>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.launch.step2")}</Typography>
          <Typography className={light.pageSubtitleWhite}>
            {i18next.t("light.launch.evalTypeQuestion")}
          </Typography>
        </Box>
      </Box>
      <Box className={light.pageInner} style={{ maxWidth: "1100px", marginTop: "-0.75rem" }}>
        <Box className={light.card} style={{ overflow: "hidden", padding: 0 }}>
          {stepper}
          <Box style={{ padding: "1.25rem" }}>

        {tpSummary}

        {/* Dossier type cards */}
        {dossierTypes.length === 0 ? (
          <Box className={s.emptyState}>{i18next.t("light.launch.noEvalTypes")}</Box>
        ) : (
          <Box className={s.dossierList}>
            {dossierTypes.map((type) => {
              const isChecked = selectedTypes.some((t) => t.code === type.code);
              return (
                <Box
                  key={type.code}
                  className={s.dossierCard}
                  onClick={() => handleToggleType(type)}
                  style={isChecked ? { borderColor: BRAND.primary, backgroundColor: `${BRAND.primary}05` } : undefined}
                >
                  {isChecked
                    ? <CheckCircleOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary, flexShrink: 0 }} />
                    : <RadioButtonUncheckedIcon style={{ fontSize: "20px", color: BRAND.border, flexShrink: 0 }} />
                  }
                  <Box className={s.dossierIcon}>
                    <FolderOutlinedIcon style={{ fontSize: "17px", color: BRAND.primary }} />
                  </Box>
                  <Box className={s.dossierInfo}>
                    <Typography className={s.dossierName}>{type.libelle}</Typography>
                    {type.requirements?.length > 0 && (
                      <Typography className={s.dossierReqs}>
                        {i18next.t("light.launch.docsRequired", { count: type.requirements.length })}
                      </Typography>
                    )}
                  </Box>
                  <DocsPopover requirements={type.requirements} />
                  {isChecked && !type.locked && (
                    <ExtraDocsPopover
                      typeCode={type.code}
                      requirements={type.requirements}
                      extraDocs={extraDocs}
                      onExtraDocsChange={setExtraDocs}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer with selection count + next */}
        {dossierTypes.length > 0 && (
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "1rem", borderTop: `1px solid ${BRAND.border}`, marginTop: "1rem" }}>
            <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family }}>
              {selectedTypes.length > 0
                ? `${selectedTypes.length} ${i18next.t("light.launch.evaluationsSelected")}`
                : i18next.t("light.launch.selectAtLeastOne")
              }
            </Typography>
            <button
              className={light.btnPrimary}
              onClick={handleNext}
              disabled={selectedTypes.length === 0}
              style={{ opacity: selectedTypes.length === 0 ? 0.5 : 1, cursor: selectedTypes.length === 0 ? "not-allowed" : "pointer" }}
            >
              {i18next.t("light.launch.next")} →
            </button>
          </Box>
        )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
